import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { FFC_FIND_BRANCH_URL } from '@/config';

/** One numbered step with a black circle badge (DESIGN State B). */
function Step({ index, boldKey, restKey }: { index: number; boldKey: string; restKey: string }) {
  const { t } = useTranslation();
  return (
    <li className='flex items-start gap-3.5'>
      <span className='inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#151512] text-[13px] font-bold text-white'>
        {index}
      </span>
      <div className='text-[14px] leading-[1.5] text-[#4A4A44]'>
        <b className='text-[#1B1B18]'>{String(t(boldKey))}</b> {String(t(restKey))}
      </div>
    </li>
  );
}

/**
 * State B — pre-activation card (DESIGN §State B / screenshot 04). Shown when
 * the merchant is enrolled but not yet FFC-activated (`!ffc_activated`).
 * Replaces the whole tab body with one centered card (max-width 720px).
 */
function AdvancePreActivationCard() {
  const { t } = useTranslation();
  return (
    <div
      className='mx-auto mt-2 max-w-[720px] rounded-2xl border border-solid border-[#EBEBE6] bg-white px-10 py-9'
      data-testid='advance-cod-pre-activation'
    >
      <div className='flex items-center gap-3'>
        <span className='inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FCF3DD]'>
          <AlertTriangle className='h-[22px] w-[22px]' style={{ color: '#B7791F' }} strokeWidth={2} />
        </span>
        <div>
          <div className='text-[19px] font-extrabold tracking-[-0.3px] text-[#1B1B18]'>
            {String(t('advance_cod_activation_title'))}
          </div>
          <div className='mt-0.5 text-[13.5px] text-[#9A9A92]'>
            {String(t('advance_cod_activation_subtitle'))}
          </div>
        </div>
      </div>

      <ol className='mt-6 flex list-none flex-col gap-4 p-0'>
        <Step index={1} boldKey='advance_cod_activation_step1_bold' restKey='advance_cod_activation_step1_rest' />
        <Step index={2} boldKey='advance_cod_activation_step2_bold' restKey='advance_cod_activation_step2_rest' />
        <Step index={3} boldKey='advance_cod_activation_step3_bold' restKey='advance_cod_activation_step3_rest' />
      </ol>

      <a
        href={FFC_FIND_BRANCH_URL}
        target='_blank'
        rel='noopener noreferrer'
        className='mt-[26px] inline-flex items-center gap-2 rounded-[11px] bg-[rgb(237,224,74)] px-5 py-3 text-[14.5px] font-bold text-[#1B1B18] transition-colors hover:bg-[rgb(230,217,60)]'
      >
        {String(t('advance_cod_find_branch'))}
        <ArrowRight className='h-4 w-4 rtl:rotate-180' strokeWidth={2.2} />
      </a>
    </div>
  );
}

export default AdvancePreActivationCard;
